'use client';

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { TrendingUp, Loader2, ChevronDown, Play, Inbox, Sparkles, X } from 'lucide-react';
import { RecommendationCard, type ImproveRecommendation } from '@/components/improve/RecommendationCard';
import { ImproveAssistant } from '@/components/improve/ImproveAssistant';

interface Overview {
  byStatus: Record<string, number>;
  byBusiness: Array<{ business: string; proposed: number; approved: number; total: number }>;
  executedThisWeek: number;
}

function CountChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="glass-card px-4 py-3">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">{label}</p>
      <span className={`text-xl font-bold ${color}`}>{value}</span>
    </div>
  );
}

export default function ImprovePage() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [recs, setRecs] = useState<ImproveRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState<string>('');
  const [domains, setDomains] = useState<string[]>([]);
  const [showRunner, setShowRunner] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [runSite, setRunSite] = useState('');
  const [running, setRunning] = useState(false);

  const loadOverview = useCallback(() => {
    fetch('/api/improve/overview')
      .then(r => r.json())
      .then(d => { if (!d.error) setOverview(d); })
      .catch(() => {});
  }, []);

  const loadRecs = useCallback((biz: string) => {
    setLoading(true);
    const qs = biz ? `?business=${encodeURIComponent(biz)}` : '';
    fetch(`/api/improve/recommendations${qs}`)
      .then(r => r.json())
      .then(d => setRecs(d.recommendations || []))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadRecs(business); }, [business, loadRecs]);
  useEffect(() => {
    fetch('/api/seo/domains')
      .then(r => r.json())
      .then(d => {
        const ds: string[] = d.domains || [];
        setDomains(ds);
        if (ds.length && !runSite) setRunSite(ds[0]);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChanged = (updated: Partial<ImproveRecommendation> & { id: string }) => {
    setRecs(prev => prev.map(r => (r.id === updated.id ? { ...r, ...updated } : r)));
    loadOverview();
  };

  const runAnalyzer = async () => {
    if (!runSite) return;
    setRunning(true);
    toast.info(`Analyzing ${runSite} — CTR gaps + agent title rewrites (a few minutes)…`);
    try {
      const res = await fetch('/api/improve/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business: runSite }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.created > 0) {
        toast.success(`${data.created} recommendation${data.created === 1 ? '' : 's'} proposed for ${runSite}`);
      } else {
        toast.info(`No CTR opportunities met the bar for ${runSite} (${data.opportunities ?? 0} candidates scanned)`);
      }
      if (data.errors?.length) toast.warning(`Run finished with notes: ${data.errors[0]}`, { duration: 8000 });
      setShowRunner(false);
      loadOverview();
      loadRecs(business);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Analyzer failed', { duration: 10000 });
    } finally {
      setRunning(false);
    }
  };

  const counts = overview?.byStatus || {};

  return (
    <div className="p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <TrendingUp className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Improve</h1>
            <p className="text-xs text-gray-500">Measure → propose → you approve → execute. Nothing ships without you.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={business}
              onChange={e => setBusiness(e.target.value)}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer max-w-[240px]"
            >
              <option value="">All businesses</option>
              {(overview?.byBusiness || []).map(b => (
                <option key={b.business} value={b.business}>
                  {b.business}{b.proposed ? ` · ${b.proposed} pending` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          <button
            onClick={() => setShowRunner(v => !v)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-sm text-gray-300 hover:text-white hover:border-cyan-500/40 transition-colors"
          >
            <Play className="w-4 h-4" />
            Run analyzer
          </button>
          <button
            onClick={() => setShowAssistant(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
              showAssistant
                ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300'
                : 'border-white/[0.08] bg-[#0d1117]/85 text-gray-300 hover:text-white hover:border-cyan-500/40'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Assistant
          </button>
        </div>
      </div>

      {/* Analyzer runner */}
      {showRunner && (
        <div className="mb-5 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-4 flex items-center gap-3 flex-wrap">
          <p className="text-xs text-gray-400">SEO CTR analyzer — pick a GSC site:</p>
          <div className="relative">
            <select
              value={runSite}
              onChange={e => setRunSite(e.target.value)}
              disabled={running}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {domains.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          <button
            onClick={runAnalyzer}
            disabled={running || !runSite}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {running ? 'Analyzing… (a few minutes)' : 'Run'}
          </button>
        </div>
      )}

      {/* Assistant — floating pop-up over the page (Boss: keep the proposals
          visible while chatting about them; never navigate away) */}
      {showAssistant && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(560px,calc(100vw-2rem))] h-[min(640px,calc(100vh-6rem))] rounded-2xl border border-cyan-500/30 bg-[#0d1117] shadow-2xl shadow-black/60 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.08] bg-white/[0.03] shrink-0">
            <span className="text-sm font-medium text-cyan-300 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Improve Assistant</span>
            <button onClick={() => setShowAssistant(false)} className="text-gray-500 hover:text-white transition-colors" aria-label="Close assistant">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <ImproveAssistant overview={overview} recs={recs} />
          </div>
        </div>
      )}

      {/* Counts */}
      <div className="grid grid-cols-3 gap-3 mb-6 max-w-md">
        <CountChip label="Proposed" value={counts.proposed || 0} color="text-blue-300" />
        <CountChip label="Approved" value={counts.approved || 0} color="text-green-300" />
        <CountChip label="Executed this week" value={overview?.executedThisWeek || 0} color="text-cyan-300" />
      </div>

      {/* Inbox */}
      {loading && (
        <div className="flex items-center justify-center h-60">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
      )}

      {!loading && recs.length === 0 && (
        <div className="text-center py-20 rounded-xl border border-white/[0.08] bg-white/[0.05]/20">
          <Inbox className="h-12 w-12 mx-auto text-gray-700 mb-4" />
          <p className="text-gray-300 font-medium mb-1">Nothing to review</p>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            The loop turns your GSC data into concrete proposed changes that only apply after you approve them —
            hit <span className="text-cyan-400">Run analyzer</span> to scan a site for CTR opportunities.
          </p>
        </div>
      )}

      {!loading && recs.length > 0 && (
        <div className="space-y-4">
          {recs.map(r => (
            <RecommendationCard key={r.id} rec={r} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}
